/**
 * Transaction List Page
 * 
 * Displays filterable, paginated list of transactions.
 * Uses useTransactionData hook for clean separation of concerns.
 */

import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, type Column, Pagination, type SortConfig } from '@/components/common/Table';
import { TransactionTypeBadge, SourceSystemBadge } from '@/components/common/Badge';
import { ActionMenu, type ActionMenuItem } from '@/components/common/ActionMenu';
import { TransactionFilters } from '@/components/transactions/TransactionFilters';
import { CreateTransactionModal } from '@/components/transactions/CreateTransactionModal';
import { useTransactionData } from '@/app/hooks/useTransactionData';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import type { TransactionResponse, TransactionQueryParams } from '@/app/types/transactions';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';

import './TransactionList.scss';

const TransactionList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActions } = useHeaderActions();
  
  const {
    transactions,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    error,
    applyFilters,
    goToPage,
    changePageSize,
    resetFilters,
    refresh,
    pageSizeOptions,
  } = useTransactionData();

  // Adjustment modal state
  const [adjustmentModal, setAdjustmentModal] = useState<{
    isOpen: boolean;
    transactionId: string;
    userId: number;
    eligibleParticipantId: number | string | null;
    partnerProductId: number | null;
  }>({ isOpen: false, transactionId: '', userId: 0, eligibleParticipantId: null, partnerProductId: null });

  const openAdjustmentModal = useCallback((tx: TransactionResponse) => {
    setAdjustmentModal({
      isOpen: true,
      transactionId: tx.transaction_id,
      userId: tx.community_ref_user_id,
      eligibleParticipantId: tx.eligible_participant_id,
      partnerProductId: tx.partner_product_id,
    });
  }, []);

  const closeAdjustmentModal = useCallback(() => {
    setAdjustmentModal({ isOpen: false, transactionId: '', userId: 0, eligibleParticipantId: null, partnerProductId: null });
  }, []);

  const handleAdjustmentSuccess = useCallback(() => {
    // Refresh the list after creating an adjustment
    applyFilters(filters);
  }, [applyFilters, filters]);

  // Build action menu items for a transaction row
  const getRowActions = useCallback((tx: TransactionResponse): ActionMenuItem[] => [
    {
      label: 'View Details',
      icon: '👁',
      onClick: () => navigate(`/transactions/${tx.transaction_id}`),
    },
    {
      label: 'Add Adjustment',
      icon: '±',
      onClick: () => openAdjustmentModal(tx),
    },
  ], [navigate, openAdjustmentModal]);
  
  // Set header actions
  useEffect(() => {
    setActions(
      <button 
        className="btn-refresh"
        onClick={() => applyFilters(filters)}
        disabled={isLoading}
      >
        ↻ Refresh
      </button>
    );
    return () => setActions(null);
  }, [setActions, applyFilters, filters, isLoading]);

  // Route change detection - refetch when returning to list page
  const prevPathnameRef = React.useRef<string>('');
  useEffect(() => {
    const isReturningToList = location.pathname === '/transactions' && 
                              prevPathnameRef.current !== '/transactions' &&
                              prevPathnameRef.current !== '';
    
    if (isReturningToList && !isLoading) {
      refresh();
    }
    
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, refresh, isLoading]);

  // Sort state (client-side for now)
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });

  // Handle filter changes (local state until search)
  const [localFilters, setLocalFilters] = React.useState<TransactionQueryParams>(filters);
  
  // Sync local filters when Redux filters change
  React.useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Sort transactions client-side
  const sortedTransactions = useMemo(() => {
    if (!sortConfig.direction || !sortConfig.key) return transactions;
    
    return [...transactions].sort((a, b) => {
      const aVal = a[sortConfig.key as keyof TransactionResponse];
      const bVal = b[sortConfig.key as keyof TransactionResponse];
      
      // Handle different types
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        // Check if it's a date string
        if (sortConfig.key.includes('date') || sortConfig.key.includes('at')) {
          const aDate = new Date(aVal).getTime();
          const bDate = new Date(bVal).getTime();
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
        return sortConfig.direction === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return 0;
    });
  }, [transactions, sortConfig]);

  const handleFiltersChange = useCallback((newFilters: TransactionQueryParams) => {
    setLocalFilters(newFilters);
  }, []);

  const handleSearch = useCallback(() => {
    applyFilters(localFilters);
  }, [applyFilters, localFilters]);

  // Log search audit after results return with accurate count
  const prevLoadingRef = React.useRef(isLoading);
  useEffect(() => {
    // Detect transition from loading to not loading (data just loaded)
    if (prevLoadingRef.current && !isLoading) {
      const filtersToLog: Record<string, unknown> = {};
      if (filters.community_ref_user_id) filtersToLog.community_ref_user_id = filters.community_ref_user_id;
      if (filters.from_date) filtersToLog.from_date = filters.from_date;
      if (filters.to_date) filtersToLog.to_date = filters.to_date;
      if (filters.time_offset) filtersToLog.time_offset = filters.time_offset;
      if (filters.transaction_type) filtersToLog.transaction_type = filters.transaction_type;
      if (filters.eligible_participant_id) filtersToLog.eligible_participant_id = filters.eligible_participant_id;
      if (filters.transaction_id) filtersToLog.transaction_id = filters.transaction_id;
      if (filters.exploration_signup_id) filtersToLog.exploration_signup_id = filters.exploration_signup_id;
      if (filters.partner_product_id) filtersToLog.partner_product_id = filters.partner_product_id;
      if (filters.source_system) filtersToLog.source_system = filters.source_system;
      
      auditService.logRecordSearch('transaction', filtersToLog, totalCount, '/transactions')
        .catch((err) => logger.warn('Failed to log record search', { error: err }));
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, totalCount, filters]);

  const handleClear = useCallback(() => {
    resetFilters();
    setLocalFilters({ limit: 100, offset: 0 });
  }, [resetFilters]);

  const handleRowClick = useCallback((transaction: TransactionResponse) => {
    navigate(`/transactions/${transaction.transaction_id}`);
  }, [navigate]);

  // Copy TX ID to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // Table columns
  const columns: Column<TransactionResponse>[] = [
    {
      key: 'transaction_id',
      header: 'Transaction ID',
      width: '320px',
      sortable: true,
      render: (tx) => (
        <span 
          className="tx-id" 
          title="Click to copy"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(tx.transaction_id);
          }}
        >
          {tx.transaction_id}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      width: '180px',
      sortable: true,
      render: (tx) => new Date(tx.created_at).toLocaleString(),
    },
    {
      key: 'community_ref_user_id',
      header: 'User ID',
      width: '100px',
      sortable: true,
      render: (tx) => (
        <a
          href={`#/users/${tx.community_ref_user_id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/users/${tx.community_ref_user_id}`);
          }}
          className="user-id-link"
        >
          {tx.community_ref_user_id}
        </a>
      ),
    },
    {
      key: 'transaction_type',
      header: 'Type',
      width: '120px',
      sortable: true,
      render: (tx) => <TransactionTypeBadge type={tx.transaction_type} />,
    },
    {
      key: 'point_value',
      header: 'Points',
      width: '100px',
      sortable: true,
      align: 'right',
      render: (tx) => (
        <span className={`points ${tx.point_value >= 0 ? 'positive' : 'negative'}`}>
          {tx.point_value >= 0 ? '+' : ''}{tx.point_value.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'source_system',
      header: 'Source',
      width: '120px',
      sortable: true,
      render: (tx) => <SourceSystemBadge source={tx.source_system} />,
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (tx) => (
        <ActionMenu
          items={getRowActions(tx)}
          triggerLabel="Actions"
          triggerIcon="▾"
        />
      ),
    },
  ];

  return (
    <div className="transaction-list-page">
      <TransactionFilters
        filters={localFilters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={handleSearch}>Retry</button>
        </div>
      )}

      <div className="results-summary">
        {!isLoading && (
          <span>{totalCount.toLocaleString()} transaction{totalCount !== 1 ? 's' : ''} found</span>
        )}
      </div>

      <Table<TransactionResponse>
        data={sortedTransactions}
        columns={columns}
        keyExtractor={(tx) => tx.transaction_id}
        loading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No transactions found matching your criteria"
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalCount}
        itemsPerPage={pageSize}
        onPageChange={goToPage}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={changePageSize}
      />

      {/* Adjustment Modal */}
      <CreateTransactionModal
        isOpen={adjustmentModal.isOpen}
        onClose={closeAdjustmentModal}
        onSuccess={handleAdjustmentSuccess}
        prefillUserId={adjustmentModal.userId}
        prefillOriginalTxId={adjustmentModal.transactionId}
        prefillEligibleParticipantId={adjustmentModal.eligibleParticipantId}
        prefillPartnerProductId={adjustmentModal.partnerProductId}
      />
    </div>
  );
};

export default TransactionList;
